import { DateEditorWrapperV16 } from '@/components/UI/SpreadSheet/editors-v16/DateEditorWrapperV16';
import type { CustomColumnSettingsV16 } from '@/components/UI/SpreadSheet/SpreadsheetV16.model';
import type { SpreadsheetV16Settings } from '@/components/UI/SpreadSheet/SpreadsheetV16.model';
import { _isEmptyString, _isNotEmpty, _notNil } from '@/littledash';
import { MAX_CAGE_POPULATION } from '@/constants/cage';
import type { Animal, AnimalSpecies, AnimalStrain } from '@/model/Animal.model';
import type { ID } from '@/model/Common.model';
import type { MetadataField, MetadataFieldValueTypes, MetadataFieldWithValue } from '@/model/Metadata.model';
import { defaultMetadataToColumnSettingMapperV16, formatColumnSettingsDataValueV16 } from '@/support/hot-v16';
import { DateInputUtils } from '@/utils/Date.utils';
import type { EntityFieldRequiredMap } from '@/utils/entitySchema/useEntitySchema';
import { validateDob } from '@/components/Studies/Create/Steps/Animals/Animals.utils.tsx';
import type Handsontable from 'handsontable-v16';

interface AnimalSettingsResultV16 {
  settings: SpreadsheetV16Settings;
  metadata?: Array<MetadataField>;
}

export const addMetaColumnsV16 = (
  settings: SpreadsheetV16Settings,
  metaColumns: Array<MetadataField>,
  metadata: Array<MetadataField>
): AnimalSettingsResultV16 => {
  const colHeaders = [...((settings.colHeaders as Array<string>) ?? [])];
  const columns = [...(settings.columns ?? [])];

  metaColumns.forEach((columnMetadata) => {
    const { columnHeader, columnSettings } = defaultMetadataToColumnSettingMapperV16(columnMetadata);
    colHeaders.push(columnHeader);
    columns.push(columnSettings as CustomColumnSettingsV16);
  });

  const newColIDs = metaColumns.map((m) => m.id);
  const newMetadata = metadata.reduce<Array<MetadataField>>((acc, item) => {
    if (!newColIDs.includes(item.id)) {
      acc.push(item);
    }
    return acc;
  }, []);

  return {
    settings: { ...settings, columns, colHeaders },
    metadata: newMetadata,
  };
};

export const initAnimalSettingsV16 = (
  species: Array<AnimalSpecies>,
  creation: boolean,
  removeRow: boolean,
  metaColumns?: Array<MetadataField>,
  metadata?: Array<MetadataField>,
  existingSettings?: SpreadsheetV16Settings,
  entityFieldsWithRequiredPair?: EntityFieldRequiredMap
): AnimalSettingsResultV16 => {
  const dobRequired = entityFieldsWithRequiredPair?.dob ?? true;

  const dobValidater = (value: string, callback: (valid: boolean) => void) => {
    validateDob(dobRequired, value, callback);
  };

  const sexValidater = (value: string, callback: (valid: boolean) => void) => {
    callback(_notNil(value) && (value === 'Male' || value === 'Female'));
  };

  const nameValidator = (value: string, callback: (valid: boolean) => void) => {
    if (!_notNil(value) || _isEmptyString(value ?? '')) {
      callback(false);
    } else if (value.length > 40) {
      callback(false);
    } else {
      callback(true);
    }
  };

  const speciesValidator = (value: string, callback: (valid: boolean) => void) => {
    if (_isNotEmpty(value)) {
      return callback(species.some((s) => s.name === value));
    }
    return callback(true);
  };

  /**
   * Strain validator: uses `this` context (CellProperties) to read species from the same row.
   * - Species selected + strain empty -> INVALID
   * - Species selected + strain filled -> check strain belongs to that species
   * - Species empty + strain empty -> VALID
   * - Species empty + strain filled -> INVALID (strain without species makes no sense)
   */
  function strainValidator(this: unknown, value: string, callback: (valid: boolean) => void) {
    const hot = (this as { instance?: Handsontable.Core }).instance;
    const row = (this as { row?: number }).row;

    if (!_notNil(hot) || !_notNil(row)) {
      return callback(true);
    }

    const speciesCol = hot.propToCol('species');
    const selectedSpecies = hot.getDataAtCell(row, speciesCol as number) as string;
    const hasSpecies = _notNil(selectedSpecies) && _isNotEmpty(selectedSpecies);
    const hasStrain = _notNil(value) && _isNotEmpty(value);

    if (!hasSpecies && !hasStrain) {
      return callback(true);
    }

    if (!hasSpecies && hasStrain) {
      return callback(false);
    }

    if (hasSpecies && !hasStrain) {
      return callback(false);
    }

    // Both species and strain are filled -- check strain belongs to the selected species
    const validStrains = species.reduce<Array<string>>((acc, sp) => {
      if (sp.name === selectedSpecies && _notNil(sp.strains) && _isNotEmpty(sp.strains)) {
        sp.strains.forEach((strain: AnimalStrain) => {
          acc.push(strain.name);
        });
      }
      return acc;
    }, []);

    return callback(validStrains.includes(value));
  }

  const hiddenColumns = creation ? [0, 1, 3] : [0, 2, 3];
  const settings: SpreadsheetV16Settings = existingSettings ?? {
    columns: [
      { data: 'id', type: 'numeric', width: 150, readOnly: true },
      { data: 'cage_catalog', type: 'text', width: 150, readOnly: true },
      { data: 'cage_name', type: 'text', width: 150, readOnly: true },
      { data: 'catalog', type: 'text', width: 150, readOnly: true },
      {
        data: 'name',
        type: 'text',
        width: 150,
        validator: nameValidator,
        validationErrorMessage: (value: unknown) => {
          const strValue = typeof value === 'string' ? value : '';
          if (!_notNil(strValue) || _isEmptyString(strValue ?? '')) {
            return 'Required Field';
          }
          if (strValue.length > 40) {
            return 'Must have a max length of 40 characters';
          }
          return 'Required Field';
        },
      },
      {
        data: 'sex',
        type: 'dropdown',
        source: ['Male', 'Female'],
        width: 150,
        validator: sexValidater,
      },
      {
        data: 'dob',
        type: DateEditorWrapperV16.type,
        dateFormat: DateInputUtils.hotDatePattern,
        width: 150,
        validator: dobValidater,
        validationErrorMessage: () =>
          dobRequired
            ? 'Required in YYYY-MM-DD and must not be a future date.'
            : 'Must be in YYYY-MM-DD format and not a future date if provided.',
      },
      {
        data: 'species',
        type: 'dropdown',
        width: 150,
        source: species.map((s) => s.name),
        validator: speciesValidator,
        linkedValidationColumns: ['strain'],
      },
      {
        data: 'strain',
        type: 'dropdown',
        width: 150,
        strict: true,
        validator: strainValidator,
        source(_query: string, process: (result: Array<string>) => void) {
          // Dynamic source function: reads species for this row and returns matching strains
          // `this` is the CellProperties object with `instance` (Handsontable.Core) and `row`
          const hot = (this as { instance?: Handsontable.Core }).instance;
          const row = (this as { row?: number }).row;
          if (_notNil(hot) && _notNil(row)) {
            const speciesCol = hot.propToCol('species');
            const selectedSpecies = hot.getDataAtCell(row, speciesCol as number) as string;
            const strains = species.reduce<Array<string>>((acc, sp) => {
              if (sp.name === selectedSpecies && _notNil(sp.strains) && _isNotEmpty(sp.strains)) {
                sp.strains.forEach((strain: AnimalStrain) => {
                  acc.push(strain.name);
                });
              }
              return acc;
            }, []);
            process(strains);
          } else {
            process([]);
          }
        },
        validationErrorMessage: () =>
          'Strain is required when a Species is selected, otherwise it should be left blank.',
      },
    ] as Array<CustomColumnSettingsV16>,
    hiddenColumns: { columns: hiddenColumns },
    colHeaders: ['ID', 'Cage No.', 'Cage Name', 'Animal No.', 'Animal Name', 'Sex', 'DOB', 'Species', 'Strain'],
    columnSorting: false,
    className: 'htMiddle',
    manualColumnResize: true,
    rowHeights: 50,
    height: 500,
    rowHeaders: true,
  };

  if (_notNil(existingSettings) && _notNil(settings.columns)) {
    const speciesCol = settings.columns.find((col) => col.data === 'species');
    if (_notNil(speciesCol)) {
      speciesCol.source = species.map((s) => s.name);
      speciesCol.validator = speciesValidator;
    }
  }

  if (_isNotEmpty(metaColumns) && _notNil(metadata)) {
    return addMetaColumnsV16(settings, metaColumns, metadata);
  }

  return { settings };
};

// ─── Initial data builder ────────────────────────────────────────

/**
 * V16 version of returnInitialMetaValues — converts API metadata to flat key-value map
 * using the v16 column data encoding.
 */
const returnInitialMetaValuesV16 = (metadata: Array<MetadataFieldWithValue>): Record<string, MetadataFieldValueTypes> =>
  metadata.reduce<Record<string, MetadataFieldValueTypes>>((acc, meta) => {
    const title = formatColumnSettingsDataValueV16(meta.title);
    if (Array.isArray(meta.value)) {
      acc[title] = meta.value.join(',');
    } else {
      acc[title] = meta.value;
    }
    return acc;
  }, {});

interface BulkAnimalRowV16 {
  id: ID;
  catalog: string;
  name: string;
  dob: string | null;
  cage_catalog: string;
  cage_name: string;
  sex?: string;
  species?: string;
  strain?: string;
  meta: Record<string, MetadataFieldValueTypes>;
}

export const initialBulkAnimalDataV16 = (
  animalData: Array<Animal>,
  speciesObj: Array<AnimalSpecies>
): Array<BulkAnimalRowV16> =>
  animalData.reduce<Array<BulkAnimalRowV16>>((acc, animalItem) => {
    const { id, catalog, name, dob, sex, strain: animalStrain, metadata: animalMetadata } = animalItem;

    // Resolve cage info — handle both nested and flat structures
    const rawAnimal = animalItem as unknown as Record<string, unknown>;
    const collection = rawAnimal.collection as { data?: { catalog: string; name: string } } | undefined;
    const cage = rawAnimal.cage as { catalog: string; name: string } | undefined;
    const innerCollection = collection?.data ?? cage;

    const innerMeta = ((rawAnimal.meta as { data?: Array<MetadataFieldWithValue> })?.data ?? animalMetadata) as
      | Array<MetadataFieldWithValue>
      | undefined;

    const row: BulkAnimalRowV16 = {
      id,
      catalog: catalog ?? '',
      name: name ?? '',
      dob,
      cage_catalog: innerCollection?.catalog ?? '',
      cage_name: innerCollection?.name ?? '',
      meta: {},
    };

    if (_notNil(innerMeta) && _isNotEmpty(innerMeta)) {
      row.meta = returnInitialMetaValuesV16(innerMeta);
    }

    if (_notNil(sex)) {
      row.sex = sex === 'm' ? 'Male' : 'Female';
    }

    const strainId = (rawAnimal as { strain_id?: number }).strain_id;
    if (_notNil(strainId)) {
      speciesObj.forEach((sp) => {
        sp.strains.forEach((s: AnimalStrain) => {
          if (s.id === strainId) {
            row.species = sp.name;
            row.strain = s.name;
          }
        });
      });
    } else if (_notNil(animalStrain)) {
      const strainObj = animalStrain as unknown as { species: { name: string }; name: string };
      row.species = strainObj.species.name;
      row.strain = strainObj.name;
    }

    acc.push(row);
    return acc;
  }, []);

// ─── Species/Strain dropdown helper ──────────────────────────────

export const updateStrainSelectDropdownV16 = (
  hot: Handsontable.Core,
  species: Array<AnimalSpecies>,
  row: number,
  selectedSpecies: string
): void => {
  const col = hot.propToCol('strain');

  const strains = species.reduce<Array<string>>((acc, sp) => {
    if (sp.name === selectedSpecies && _notNil(sp.strains) && _isNotEmpty(sp.strains)) {
      sp.strains.forEach((strain: AnimalStrain) => {
        acc.push(strain.name);
      });
    }
    return acc;
  }, []);

  hot.setCellMeta(row, col as number, 'source', strains);
  hot.setCellMeta(row, col as number, 'allowEmpty', !_notNil(selectedSpecies) || _isEmptyString(selectedSpecies));
};

// ─── Cage settings & data (v16) ─────────────────────────────────

interface CageSettingsResultV16 {
  settings: SpreadsheetV16Settings;
  metadata?: Array<MetadataField>;
}

const cageNameValidatorV16 = (value: unknown, callback: (valid: boolean) => void): void => {
  const strValue = typeof value === 'string' ? value : '';
  callback(!_isEmptyString(strValue) && strValue.length <= 50);
};

const populationValidatorV16 = (value: unknown, callback: (valid: boolean) => void): void => {
  const num = Number(value);
  callback(
    !isNaN(num) && value !== null && value !== undefined && value !== '' && num >= 0 && num <= MAX_CAGE_POPULATION
  );
};

export const initBulkCageSettingsV16 = (
  metaColumns: Array<MetadataField>,
  metadata: Array<MetadataField>
): CageSettingsResultV16 => {
  const settings: SpreadsheetV16Settings = {
    columns: [
      { data: 'catalog', width: 150, readOnly: true },
      {
        data: 'name',
        type: 'text',
        width: 150,
        validator: cageNameValidatorV16,
        validationErrorMessage: (value: unknown) => `${value ?? 'Value'} name must have a maximum of 50 characters`,
        linkedValidationColumns: ['population'],
      },
      {
        data: 'population',
        type: 'numeric',
        width: 150,
        readOnly: true,
        validator: populationValidatorV16,
        validationErrorMessage: (value: unknown) =>
          `${value ?? 'Value'} must be a number with a maximum of ${MAX_CAGE_POPULATION}`,
        linkedValidationColumns: ['name'],
      },
    ] as Array<CustomColumnSettingsV16>,
    colHeaders: ['No', 'Cage Name', 'Population'],
    hiddenColumns: { columns: [0] },
    columnSorting: false,
    contextMenu: false,
    className: 'htMiddle',
    manualColumnResize: true,
    rowHeights: 50,
    height: 500,
    rowHeaders: true,
  };

  if (_isNotEmpty(metaColumns)) {
    return addMetaColumnsV16(settings, metaColumns, metadata);
  }

  return { settings };
};

interface BulkCageRowV16 {
  id: ID;
  catalog: string;
  name: string;
  population: number;
  meta: Record<string, MetadataFieldValueTypes>;
}

export const initialBulkCageDataV16 = (selectedCages: Array<unknown>): Array<BulkCageRowV16> =>
  (selectedCages ?? []).reduce<Array<BulkCageRowV16>>((acc, rawCage) => {
    const cage = rawCage as {
      id: ID;
      name: string;
      catalog: string;
      counts: { animals: number };
      meta: { data?: Array<MetadataFieldWithValue> };
    };

    const row: BulkCageRowV16 = {
      id: cage.id,
      catalog: cage.catalog ?? '',
      name: cage.name ?? '',
      population: cage.counts?.animals ?? 0,
      meta: {},
    };

    const innerMeta = cage.meta?.data;
    if (_notNil(innerMeta) && _isNotEmpty(innerMeta)) {
      row.meta = returnInitialMetaValuesV16(innerMeta);
    }

    acc.push(row);
    return acc;
  }, []);
